import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  AlertTriangle, Clock, MapPin, User, Calendar, CheckCircle2,
  XCircle, Phone, RefreshCw, HandHelping, UserX, ChevronRight,
  Bell, Users
} from 'lucide-react';

export default function ShiftCoverage({ userRole = 'caregiver' }) {
  const [activeTab, setActiveTab] = useState('available');
  const [availableShifts, setAvailableShifts] = useState([]);
  const [myCallouts, setMyCallouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingId, setClaimingId] = useState(null);

  const fetchAvailableShifts = useCallback(async () => {
    try {
      const response = await api.get('/shift-coverage/available-shifts');
      setAvailableShifts(response.data.available_shifts || []);
    } catch (error) {
      console.error('Failed to fetch available shifts:', error);
    }
  }, []);

  const fetchMyCallouts = useCallback(async () => {
    try {
      const response = await api.get('/shift-coverage/my-callouts');
      setMyCallouts(response.data.callouts || []);
    } catch (error) {
      console.error('Failed to fetch callouts:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAvailableShifts(), fetchMyCallouts()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAvailableShifts, fetchMyCallouts]);

  const handleClaimShift = async (calloutId) => {
    try {
      setClaimingId(calloutId);
      await api.post(`/shift-coverage/claim/${calloutId}`, { notes: '' });
      toast.success('Shift claimed successfully! Check your schedule.');
      fetchAvailableShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim shift');
    } finally {
      setClaimingId(null);
    }
  };

  const handleCancelCallout = async (calloutId) => {
    try {
      await api.delete(`/shift-coverage/callout/${calloutId}`);
      toast.success('Callout cancelled');
      fetchMyCallouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel callout');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      return `${Math.floor(diffHours / 24)}d ago`;
    } catch {
      return '';
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      sick: 'Called out sick',
      emergency: 'Emergency',
      personal: 'Personal',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      covered: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    const labels = {
      pending: 'Needs Coverage',
      covered: 'Covered',
      cancelled: 'Cancelled'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Shift Coverage
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Pick up available shifts or manage your callouts
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchAvailableShifts();
            fetchMyCallouts();
          }}
          className="gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('available')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'available'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <HandHelping className="w-4 h-4" />
            Available Shifts
            {availableShifts.length > 0 && (
              <span className="bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">
                {availableShifts.length}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('my-callouts')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'my-callouts'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <UserX className="w-4 h-4" />
            My Callouts
          </div>
        </button>
      </div>

      {/* Available Shifts Tab */}
      {activeTab === 'available' && (
        <div className="space-y-4">
          {availableShifts.length > 0 ? (
            availableShifts.map((shift) => (
              <Card
                key={shift.callout_id}
                className="p-5 border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:shadow-md transition-shadow"
                data-testid={`available-shift-${shift.callout_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-700">
                        Coverage Needed
                      </span>
                      <span className="text-xs text-amber-600">
                        {formatTimeAgo(shift.created_at)}
                      </span>
                    </div>

                    <h3 className="font-semibold text-gray-900 text-lg">
                      {shift.client_name}
                    </h3>

                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(shift.shift_date)}</span>
                      </div>
                      
                      {shift.client_address && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{shift.client_address}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>Originally: {shift.original_employee}</span>
                        <span className="text-amber-600">({getReasonLabel(shift.reason)})</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleClaimShift(shift.callout_id)}
                    disabled={claimingId === shift.callout_id}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 gap-2"
                    data-testid={`claim-shift-${shift.callout_id}`}
                  >
                    {claimingId === shift.callout_id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    Claim Shift
                  </Button>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-8 text-center border border-gray-200">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <HandHelping className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-medium text-gray-900">No Available Shifts</h3>
              <p className="text-sm text-gray-500 mt-1">
                There are no shifts needing coverage right now.
              </p>
            </Card>
          )}
        </div>
      )}

      {/* My Callouts Tab */}
      {activeTab === 'my-callouts' && (
        <div className="space-y-4">
          {myCallouts.length > 0 ? (
            myCallouts.map((callout) => (
              <Card
                key={callout.id}
                className="p-5 border border-gray-200"
                data-testid={`my-callout-${callout.id}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-gray-900">
                        {callout.client_name}
                      </h3>
                      {getStatusBadge(callout.status)}
                    </div>

                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span>{formatDate(callout.shift_date)}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <AlertTriangle className="w-4 h-4 text-gray-400" />
                        <span>Reason: {getReasonLabel(callout.reason)}</span>
                      </div>

                      {callout.covered_by && (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Covered by: {callout.covered_by}</span>
                        </div>
                      )}

                      <div className="text-xs text-gray-400">
                        Called out {formatTimeAgo(callout.created_at)}
                      </div>
                    </div>
                  </div>

                  {callout.status === 'pending' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelCallout(callout.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                  )}
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-8 text-center border border-gray-200">
              <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="font-medium text-gray-900">No Callouts</h3>
              <p className="text-sm text-gray-500 mt-1">
                You have not called out of any shifts.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// Callout Form Component (for reporting a callout)
export function CalloutForm({ shiftId, shiftDate, clientName, onSuccess, onCancel }) {
  const [reason, setReason] = useState('sick');
  const [reasonDetails, setReasonDetails] = useState('');
  const [notifySupervisors, setNotifySupervisors] = useState(true);
  const [findCoverage, setFindCoverage] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await api.post('/shift-coverage/callout', {
        shift_id: shiftId,
        reason,
        reason_details: reasonDetails,
        notify_supervisors: notifySupervisors,
        find_coverage: findCoverage,
        send_sms: sendSms
      });

      let notificationMsg = `Callout reported. ${response.data.workers_notified} workers and ${response.data.supervisors_notified} supervisors notified.`;
      if (response.data.sms_sent > 0) {
        notificationMsg += ` ${response.data.sms_sent} SMS sent.`;
      }
      toast.success(notificationMsg);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report callout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <div className="flex items-center gap-2 text-amber-800">
          <AlertTriangle className="w-5 h-5" />
          <span className="font-medium">Calling out of shift</span>
        </div>
        <p className="text-sm text-amber-700 mt-1">
          {clientName} - {new Date(shiftDate).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Reason for calling out
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'sick', label: 'Sick', icon: '🤒' },
            { value: 'emergency', label: 'Emergency', icon: '🚨' },
            { value: 'personal', label: 'Personal', icon: '👤' },
            { value: 'other', label: 'Other', icon: '📝' }
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setReason(opt.value)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                reason === opt.value
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className="text-lg mr-2">{opt.icon}</span>
              <span className="font-medium">{opt.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional details (optional)
        </label>
        <textarea
          value={reasonDetails}
          onChange={(e) => setReasonDetails(e.target.value)}
          placeholder="Any additional information..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          rows={3}
        />
      </div>

      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={findCoverage}
            onChange={(e) => setFindCoverage(e.target.checked)}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">Find coverage</span>
            <p className="text-sm text-gray-500">Notify other workers who can cover this shift</p>
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={notifySupervisors}
            onChange={(e) => setNotifySupervisors(e.target.checked)}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <div>
            <span className="font-medium text-gray-900">Notify supervisors</span>
            <p className="text-sm text-gray-500">Alert admins and RN supervisors</p>
          </div>
        </label>

        <label className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer">
          <input
            type="checkbox"
            checked={sendSms}
            onChange={(e) => setSendSms(e.target.checked)}
            className="w-4 h-4 text-green-600 rounded"
          />
          <div>
            <span className="font-medium text-green-900">Send SMS alerts</span>
            <p className="text-sm text-green-700">Text message workers and supervisors for faster response</p>
          </div>
        </label>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="flex-1 bg-amber-600 hover:bg-amber-700"
        >
          {submitting ? 'Reporting...' : 'Report Callout'}
        </Button>
      </div>
    </form>
  );
}

// Admin Callout Dashboard Component
export function AdminCalloutDashboard() {
  const [callouts, setCallouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [smsStatus, setSmsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchCallouts = useCallback(async () => {
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/shift-coverage/admin/callouts${params}`);
      setCallouts(response.data.callouts || []);
    } catch (error) {
      console.error('Failed to fetch callouts:', error);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/shift-coverage/admin/coverage-stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchSmsStatus = useCallback(async () => {
    try {
      const response = await api.get('/shift-coverage/admin/sms-status');
      setSmsStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch SMS status:', error);
      setSmsStatus({ sms_enabled: false });
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCallouts(), fetchStats(), fetchSmsStatus()]);
      setLoading(false);
    };
    loadData();
  }, [fetchCallouts, fetchStats, fetchSmsStatus]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      covered: 'bg-green-100 text-green-700',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SMS Status Banner */}
      {smsStatus && (
        <div className={`p-3 rounded-lg flex items-center justify-between ${
          smsStatus.sms_enabled 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-amber-50 border border-amber-200'
        }`}>
          <div className="flex items-center gap-2">
            <Phone className={`w-4 h-4 ${smsStatus.sms_enabled ? 'text-green-600' : 'text-amber-600'}`} />
            <span className={`text-sm font-medium ${smsStatus.sms_enabled ? 'text-green-800' : 'text-amber-800'}`}>
              {smsStatus.sms_enabled 
                ? `SMS notifications active (from ...${smsStatus.from_number})` 
                : 'SMS not configured - add Twilio credentials to enable'
              }
            </span>
          </div>
          {smsStatus.sms_enabled && (
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
          )}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Total Callouts (30d)</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_callouts}</p>
          </Card>
          <Card className="p-4 border border-amber-200 bg-amber-50">
            <p className="text-sm text-amber-700">Pending</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
          </Card>
          <Card className="p-4 border border-green-200 bg-green-50">
            <p className="text-sm text-green-700">Covered</p>
            <p className="text-2xl font-bold text-green-600">{stats.covered}</p>
          </Card>
          <Card className="p-4 border border-purple-200 bg-purple-50">
            <p className="text-sm text-purple-700">Coverage Rate</p>
            <p className="text-2xl font-bold text-purple-600">{stats.coverage_rate}%</p>
          </Card>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600">Filter:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="covered">Covered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchCallouts}
          className="gap-2 ml-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Callouts List */}
      <div className="space-y-3">
        {callouts.length > 0 ? (
          callouts.map((callout) => (
            <Card key={callout.id} className="p-4 border border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900">{callout.employee_name}</span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {callout.employee_role}
                    </span>
                    {getStatusBadge(callout.status)}
                  </div>
                  <p className="text-sm text-gray-600">
                    Shift: {callout.client_name} - {formatDate(callout.shift_date)}
                  </p>
                  <p className="text-sm text-gray-500">
                    Reason: {callout.reason} {callout.reason_details && `- ${callout.reason_details}`}
                  </p>
                  {callout.covered_by && (
                    <p className="text-sm text-green-600 mt-1">
                      Covered by: {callout.covered_by}
                    </p>
                  )}
                </div>
                <div className="text-xs text-gray-400">
                  {formatDate(callout.created_at)}
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-8 text-center border border-gray-200">
            <p className="text-gray-500">No callouts found</p>
          </Card>
        )}
      </div>
    </div>
  );
}
